import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import { traceService } from '../../services/debug/traceService';

const log = createFileLogger('AICOMMANDS');

// ====== WEB SEARCH COMMAND ======
// Provider-agnostic web search - works with Claude (native) and Grok (/responses endpoint)
export const webSearchCommand: SelfContainedCommand = {
  id: 'web_search',
  description: `Search the Internet for information

**EXAMPLE:**

{ "query": "latest React 18 features" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  formatViewData: (result: any) => {
    // ✅ For server-side tools, view data was already captured during streaming
    // This formatViewData is mainly for the final result message
    const query = result.data?.query || '';
    const resultsCount = result.data?.search_results?.length || 0;
    
    // Note: search_results may be empty here since they were captured from stream
    // The actual results are in execution.result.view.search_results from streaming
    return {
      type: 'web_search',
      summary: `Web search completed${resultsCount > 0 ? ` (${resultsCount} sources)` : ''}`,
      data: {
        query: query,
        search_results: result.data?.search_results || [],
        serverSideTool: true
      },
      meta: {
        timestamp: Date.now()
      }
    };
  },
  
  examples: {
    good: [
      {
        description: "Find submission guidelines for a specific journal",
        input: {
          query: "JAMA manuscript submission guidelines formatting requirements",
          maxResults: 3
        },
        expectedOutput: createSuccessResult(
          'External context retrieved successfully',
          {
            success: true,
            context: "## Key Information\n- JAMA requires double-spaced text\n- Maximum 3000 words\n- References in Vancouver style",
            query: "JAMA manuscript submission guidelines formatting requirements",
            metadata: {}
          }
        )
      }
    ],
    bad: [
      {
        input: {
          query: "rules"
        },
        error: "Search query too vague",
        why: "Query 'rules' is too broad and lacks specific context about what type of rules are needed",
        fix: "Be more specific: 'submission guidelines for medical journals' or 'formatting requirements for academic papers'"
      }
    ]
  },
  
  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'What information to search for',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Search query cannot be empty';
          if (value.length > 500) return 'Search query too long (max 500 characters)';
          return null;
        }
      },
      {
        name: 'maxResults',
        type: 'number',
        description: 'Maximum search results to return (default: 5, max: 20)',
        required: false,
        defaultValue: 5,
        validator: (value: number) => {
          if (value && (value < 1 || value > 20)) return 'maxResults must be between 1 and 20';
          return null;
        }
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    // Debug logging
    log.info(`[web_search] Received params:`, params);
    log.info(`[web_search] Params keys:`, Object.keys(params || {}));
    
    const { query, maxResults = 5 } = params;
    
    // Validate query
    if (!query || typeof query !== 'string' || query.trim().length === 0) {
      log.error(`[web_search] Invalid query:`, { query, type: typeof query, params });
      return createErrorResult(
        'Invalid query',
        `Query parameter is required and must be a non-empty string. Received: ${JSON.stringify(params)}`,
        'validation',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
    
    // ✅ For CLAUDE: Server-side web search via SSE stream
    // ✅ For GROK: Execute via backend API call to /responses endpoint
    // ✅ For others: Return error (not supported)
    
    const model = (context as any).model || '';
    const isClaudeModel = model.includes('claude') || model.includes('anthropic');
    const isGrokModel = model.includes('grok') || model.includes('x.ai');
    
    // If model is empty but we're being called, assume it's supported (for Grok)
    // This is a fallback for when model info isn't passed through
    const modelUnknown = !model || model.trim().length === 0;
    const assumeGrok = modelUnknown;  // If called without model info, assume Grok (it's calling this tool)
    
    log.info(`[web_search] Model: ${model}, Claude: ${isClaudeModel}, Grok: ${isGrokModel}, assumeGrok: ${assumeGrok}`);
    log.info(`[web_search] Query: ${query}`);
    
    // Trace command start
    traceService.log({
      traceId: context.executionId,
      type: 'ai_command_start',
      data: {
        commandId: 'web_search',
        query: query.substring(0, 100),
        maxResults,
        model,
        mode: isClaudeModel ? 'claude-native' : isGrokModel ? 'grok-responses' : 'unsupported'
      }
    });
    
    try {
      if (isClaudeModel) {
        // ✅ Claude's native web search - results come via SSE stream
        if (context.onProgress) {
          context.onProgress({
            type: 'streaming_update',
            toolUseId: context.toolUseId || context.executionId,
            activity: `Web search executed by Claude (server-side)...`
          });
        }
        
        traceService.log({
          traceId: context.executionId,
          type: 'ai_command_complete',
          data: {
            commandId: 'web_search',
            success: true,
            note: 'Claude server-side tool - results captured from stream'
          },
          metadata: { executionTime: Date.now() - startTime }
        });
        
        return createSuccessResult(
          'Web search completed (Claude server-side)',
          {
            success: true,
            query: query,
            note: 'Results were captured from SSE stream',
            metadata: {
              executionTime: Date.now() - startTime,
              serverSideTool: true,
              provider: 'claude'
            }
          },
          { metadata: { executionTime: Date.now() - startTime } }
        );
      } else if (isGrokModel || assumeGrok) {
        // ✅ Grok web search via /responses endpoint
        // Return a marker that tells the backend to switch to /responses endpoint
        log.info('[web_search] Grok detected - returning marker for backend to execute via /responses');
        
        if (context.onProgress) {
          context.onProgress({
            type: 'streaming_update',
            toolUseId: context.toolUseId || context.executionId,
            activity: `Web search will be executed by Grok (server-side)...`
          });
        }
        
        traceService.log({
          traceId: context.executionId,
          type: 'ai_command_complete',
          data: {
            commandId: 'web_search',
            success: true,
            note: 'Grok web search - backend will execute via /responses endpoint'
          },
          metadata: { executionTime: Date.now() - startTime }
        });
        
        return createSuccessResult(
          'Web search will be executed by Grok',
          {
            success: true,
            query: query,
            note: 'Backend will execute via /responses endpoint',
            metadata: {
              executionTime: Date.now() - startTime,
              serverSideTool: true,
              provider: 'grok',
              requiresResponsesEndpoint: true  // ✅ Marker for backend detection
            }
          },
          { metadata: { executionTime: Date.now() - startTime } }
        );
      } else {
        // Unsupported provider
        return createErrorResult(
          'Web search not available',
          `Web search is only available for Claude and Grok models. Current model: ${model}`,
          'system',
          { 
            metadata: { 
              executionTime: Date.now() - startTime
            } 
          }
        );
      }

    } catch (error: any) {
      log.error('[web_search] Error:', error);
      
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_error',
        data: {
          commandId: 'web_search',
          success: false,
          error: error instanceof Error ? error.message : String(error)
        },
        metadata: { executionTime: Date.now() - startTime }
      });
      
      return createErrorResult(
        'Web search failed',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};


